import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from 'src/components/ui/input';
import { StringListEditor } from 'src/components/shared';
import { cn } from 'src/lib/utils';
import type { VerticalProfile, VerticalProfileWritePayload } from '../types/vertical-profile.types';
import { CREATIVE_ROLE_OPTIONS, VERTICAL_PROFILE_ARCHETYPE_OPTIONS } from '../types/vertical-profile.types';
import {
  buildVerticalProfilePayload,
  EMPTY_IMAGERY,
  validateVerticalProfileForm,
} from '../helpers/vertical-profile.helpers';
import { ImageryGuidanceEditor } from './imagery-guidance-editor';
import { SchedulerDsButton } from './scheduler-ds-button';

const labelClassName = 'text-sm font-medium text-gray-700';
const selectClassName = cn(
  'h-10 w-full appearance-none rounded border border-gray-400 bg-white pl-4 pr-10 text-base text-black',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black',
);
const textareaClassName = cn(
  'flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm',
  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm',
);

interface VerticalProfileFormProps {
  initialProfile?: VerticalProfile | null;
  isCreate?: boolean;
  isSaving?: boolean;
  submitError?: string | null;
  onSubmit: (values: VerticalProfileWritePayload) => void;
  onCancel?: () => void;
}

export function VerticalProfileForm({
  initialProfile,
  isCreate = false,
  isSaving = false,
  submitError = null,
  onSubmit,
  onCancel,
}: VerticalProfileFormProps) {
  const payload = initialProfile?.payload;
  const [profileId, setProfileId] = useState(payload?.profile_id ?? '');
  const [name, setName] = useState(payload?.name ?? '');
  const [archetype, setArchetype] = useState(payload?.archetype ?? 'hero_product');
  const [group, setGroup] = useState(payload?.group ?? '');
  const [industries, setIndustries] = useState(payload?.industries ?? []);
  const [mediaTypes, setMediaTypes] = useState(payload?.media_types ?? []);
  const [purposes, setPurposes] = useState(payload?.purposes ?? []);
  const [requiredElements, setRequiredElements] = useState(payload?.required_elements ?? []);
  const [requiredRoles, setRequiredRoles] = useState(payload?.required_roles ?? []);
  const [toneGuidance, setToneGuidance] = useState(payload?.tone_guidance ?? '');
  const [keyVisualRules, setKeyVisualRules] = useState(payload?.key_visual_rules ?? []);
  const [avoidances, setAvoidances] = useState(payload?.avoidances ?? []);
  const [imagery, setImagery] = useState(payload?.imagery ?? EMPTY_IMAGERY);
  const [copyPrompt, setCopyPrompt] = useState(payload?.copy_prompt ?? '');
  const [generationLevel, setGenerationLevel] = useState(payload?.generation_policy.level ?? 'background_only');
  const [maxGeneratedArea, setMaxGeneratedArea] = useState(payload?.generation_policy.max_generated_area_frac ?? 0.55);
  const [requireProvenance, setRequireProvenance] = useState(payload?.generation_policy.require_provenance ?? true);
  const [changeNote, setChangeNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const values: VerticalProfileWritePayload = buildVerticalProfilePayload({
      ...(isCreate ? { profile_id: profileId } : {}),
      name,
      archetype,
      group,
      industries,
      media_types: mediaTypes,
      purposes,
      required_elements: requiredElements,
      required_roles: requiredRoles,
      tone_guidance: toneGuidance,
      key_visual_rules: keyVisualRules,
      avoidances,
      imagery,
      copy_prompt: copyPrompt,
      generation_policy: {
        level: generationLevel,
        max_generated_area_frac: maxGeneratedArea,
        require_provenance: requireProvenance,
      },
      change_note: changeNote,
    });
    const error = validateVerticalProfileForm(values, { isCreate });
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    onSubmit(values);
  };

  const displayError = validationError ?? submitError;

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {displayError ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {displayError}
        </p>
      ) : null}

      {isCreate ? (
        <label className="block space-y-1">
          <span className={labelClassName}>Profile ID</span>
          <Input value={profileId} onChange={(event) => setProfileId(event.target.value)} placeholder="e.g. qsr" />
        </label>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <label className="block space-y-1">
          <span className={labelClassName}>Name</span>
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label className="relative block space-y-1">
          <span className={labelClassName}>Archetype</span>
          <select value={archetype} onChange={(event) => setArchetype(event.target.value as typeof archetype)} className={selectClassName}>
            {VERTICAL_PROFILE_ARCHETYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
        <label className="block space-y-1 md:col-span-2">
          <span className={labelClassName}>Group</span>
          <Input value={group} onChange={(event) => setGroup(event.target.value)} />
        </label>
      </div>

      <StringListEditor label="Industries" values={industries} onChange={setIndustries} />
      <StringListEditor label="Media types" values={mediaTypes} onChange={setMediaTypes} />
      <StringListEditor label="Purposes" values={purposes} onChange={setPurposes} />
      <StringListEditor label="Required elements" values={requiredElements} onChange={setRequiredElements} />
      <StringListEditor
        label="Required roles"
        values={requiredRoles}
        onChange={setRequiredRoles}
        placeholder={CREATIVE_ROLE_OPTIONS.join(', ')}
      />
      <StringListEditor label="Key visual rules" values={keyVisualRules} onChange={setKeyVisualRules} />
      <StringListEditor label="Avoidances" values={avoidances} onChange={setAvoidances} />

      <label className="block space-y-1">
        <span className={labelClassName}>Tone guidance</span>
        <textarea value={toneGuidance} onChange={(event) => setToneGuidance(event.target.value)} className={textareaClassName} />
      </label>

      <label className="block space-y-1">
        <span className={labelClassName}>Copy prompt</span>
        <textarea value={copyPrompt} onChange={(event) => setCopyPrompt(event.target.value)} className={textareaClassName} />
      </label>

      <ImageryGuidanceEditor value={imagery} onChange={setImagery} />

      <section className="space-y-3 rounded-md border border-gray-300 bg-gray-50 p-4">
        <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">Generation policy</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="relative block space-y-1">
            <span className={labelClassName}>Level</span>
            <select value={generationLevel} onChange={(event) => setGenerationLevel(event.target.value as typeof generationLevel)} className={selectClassName}>
              <option value="none">none</option>
              <option value="background_only">background_only</option>
              <option value="full">full</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className={labelClassName}>Max generated area frac</span>
            <Input type="number" step="0.01" value={maxGeneratedArea} onChange={(event) => setMaxGeneratedArea(Number(event.target.value))} />
          </label>
        </div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={requireProvenance} onChange={(event) => setRequireProvenance(event.target.checked)} className="size-4 rounded border-gray-400" />
          <span className={labelClassName}>Require provenance</span>
        </label>
      </section>

      {!isCreate ? (
        <label className="block space-y-1">
          <span className={labelClassName}>Change note</span>
          <Input value={changeNote} onChange={(event) => setChangeNote(event.target.value)} placeholder="Describe what changed in this version" />
        </label>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <SchedulerDsButton type="submit" disabled={isSaving}>
          {isSaving ? <Loader2 className="size-4 animate-spin" /> : null}
          {isCreate ? 'Create profile' : 'Save new version'}
        </SchedulerDsButton>
        {onCancel ? (
          <SchedulerDsButton type="button" variant="secondary" disabled={isSaving} onClick={onCancel}>
            Cancel
          </SchedulerDsButton>
        ) : null}
      </div>
    </form>
  );
}
